import React, { useEffect, useRef } from 'react';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import GeoJSON from 'ol/format/GeoJSON';
import { Map } from 'ol';
import { createStyleFunction } from '@/utils/map.utils'; // Ensure this path is correct
import { useLayerById } from '@/stores/mapStore';
import { useLayerFeatures } from '@/hooks/api/useMapLayers';

type AssetLayerProps = {
  map: Map;
  layerId: string;
};

export const VectorMapLayer: React.FC<AssetLayerProps> = ({ map, layerId }) => {
  const layerInstance = useRef<VectorLayer<VectorSource> | null>(null);
  const sourceInstance = useRef<VectorSource | null>(null);

  // Get layer config from store
  const layerDef = useLayerById(layerId);

  // Get GeoJSON data
  const { data, error } = useLayerFeatures(layerId);

  // ---------------------------------------------------------------------------
  //  INITIALIZATION
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map) return;

    // Create Source
    const source = new VectorSource({
      format: new GeoJSON(),
    });
    sourceInstance.current = source;

    // Create Layer
    const vectorLayer = new VectorLayer({
      source: source,
      properties: { config: layerDef },
      visible: true,
      zIndex: 0,
      declutter: true,
    });

    map.addLayer(vectorLayer);
    layerInstance.current = vectorLayer;

    // Cleanup
    return () => {
      if (layerInstance.current) {
        map.removeLayer(layerInstance.current);
        layerInstance.current = null;
        sourceInstance.current = null;
      }
    };
  }, [map, layerId]);

  // ---------------------------------------------------------------------------
  //  DATA SYNC
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const source = sourceInstance.current;
    if (!source || !data || !data.features) return;

    source.clear();

    const format = new GeoJSON();

    const features = format.readFeatures(data, {
      dataProjection: 'EPSG:4326',
      featureProjection: map.getView().getProjection() || 'EPSG:3857',
    });

    source.addFeatures(features);
  }, [data, map]);

  // ---------------------------------------------------------------------------
  //  DYNAMIC LAYER PROPERTY UPDATES
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const olLayer = layerInstance.current;
    if (!olLayer || !layerDef) return;

    // VISIBILITY
    if (olLayer.getVisible() !== layerDef.isVisible) {
      olLayer.setVisible(layerDef?.isVisible ?? true);
    }

    // OPACITY
    const targetOpacity = Number(layerDef.opacity ?? 1.0);
    if (olLayer.getOpacity() !== targetOpacity) {
      olLayer.setOpacity(targetOpacity);
    }

    // Z-INDEX
    const targetZIndex = layerDef.zIndex ?? 0;
    if (olLayer.getZIndex() !== targetZIndex) {
      olLayer.setZIndex(targetZIndex);
    }

    // MIN/MAX ZOOM
    const targetMin = layerDef.minZoom ?? 0;
    const targetMax = layerDef.maxZoom ?? Infinity;

    if (olLayer.getMinZoom() !== targetMin) olLayer.setMinZoom(targetMin - 0.5);
    if (olLayer.getMaxZoom() !== targetMax) olLayer.setMaxZoom(targetMax);
  }, [
    layerDef?.isVisible,
    layerDef?.opacity,
    layerDef?.zIndex,
    layerDef?.minZoom,
    layerDef?.maxZoom,
  ]);

  // ---------------------------------------------------------------------------
  //  STYLE UPDATES
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const olLayer = layerInstance.current;
    if (!olLayer || !layerDef?.styles) return;

    // Only update if styles actually exist/change
    const newStyle = createStyleFunction(layerDef.styles);
    olLayer.setStyle(newStyle);

    // Force redraw just in case labels need calculation
    // olLayer.changed();
  }, [layerDef?.styles]);

  if (error) {
    console.error(`Error loading layer ${layerId}:`, error);
  }

  return null;
};
